/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../WidgetTitleTruncateAPI'
], function (APIFactory, WidgetTitleTruncateAPI) {
	'use strict';

	const defaultValues = {
		truncateTitle: false
	};

	class WidgetTitleTruncate {
		constructor(options) {
			this._content = options.content;
			this._internal = options.features && options.features['Dashboard.internal'];
			this._dashboard = options.dashboardAPI;
			this._getProperty = this._getModelProp.bind(this);
			this._state = {
				initialWidgetRenderDone: false,
				truncateOn: false
			};
		}

		getExtensionHooks() {
			return {
				onInit: this._handleTitleTruncation.bind(this),
				onSetText: this._onSetText.bind(this),
				onEditStart: this._onEditStart.bind(this),
				onEditUpdate: this._onEditUpdate.bind(this),
				onEditEnd: this._onEditEnd.bind(this),
				//onModifyTextColor: this._onModifyTextColor.bind(this),
				isTextWrapOn: this._isTextWrapOn.bind(this),
				onTextWrapChanged: this._onTextWrapChanged.bind(this)
			};
		}

		setUpdateModelContent(setter) {
			this._updateModelContent = setter;
		}

		setTitleHighlighter(setter) {
			this.highlightTitle = setter;
		}

		highlightTitle() {
			if( this.highlightTitle ) {
				this.highlightTitle();
			}
		}

		getState() {
			return {
				truncateTitleOn: this._content.getPropertyValue('truncateTitle'),
				truncateTemporarilyOff: this._truncateTemporarilyOff
			};
		}

		getAPI() {
			return APIFactory.createAPI(this, [WidgetTitleTruncateAPI]);
		}

		_showTitle() {
			const titleMode = this._getProperty('titleMode');
			if (titleMode !== 'noTitle') {
				return true;
			}
			return false;
		}

		_isTextWrapOn() {
			return this._getProperty('truncateTitle') !== true;
		}

		_onTextWrapChanged(textWrapFlag) {
			this._setProperty('truncateTitle', !textWrapFlag);
		}

		_handleTitleTruncation(textEditor) {
			if (this._content) {
				this._onWidgetRenderDone();
				const showTitle = this._showTitle();
				const truncateTitle = this._getProperty('truncateTitle');
				if (showTitle && truncateTitle) {
					this._setTruncate(textEditor, true, true);
				}
				this._content.on('change:property:truncateTitle', (event) => {
					const showTitle = this._showTitle();
					if (event.info.value === true) {
						if (showTitle && !this._isTruncateStateSet()) {
							this._setTruncate(textEditor, true);
						}
					} else {
						if (showTitle && this._isTruncateStateSet()) {
							this._setTruncate(textEditor, false);
						}
					}
				});
			}
		}

		_onWidgetRenderDone() {
			const stateAPI = this._content.getFeature('state');
			if (!this._state.initialWidgetRenderDone) {
				return stateAPI.whenStatusChanges('rendered').then(() => {
					this._getProperty = this._getContentProperty.bind(this);
					this._state.initialWidgetRenderDone = true;
				});
			}
			return Promise.resolve(true);
		}

		_getContentProperty(name) {
			return this._content.getPropertyValue(name);
		}

		_getModelProp(name) {
			const boardModel = this._internal.getBoardModel();
			const id = this._content.getId();
			const widgetModel = boardModel.getWidgetModel(id);
			const value = widgetModel.get(name);
			return value !== undefined ? value : defaultValues[name];
		}

		_setProperty(name, value) {
			if (this._state.initialWidgetRenderDone) {
				this._content.setPropertyValue(name, value);
			} else {
				let stateAPI = this._content.getFeature('state');
				stateAPI.whenStatusChanges('rendered').then(() => {
					this._content.setPropertyValue(name, value);
				});
			}
		}

		_isTruncateStateSet() {
			return this._state.truncateOn;
		}

		_setTruncateState(value) {
			this._state.truncateOn = value;
		}

		_setTruncate(textEditor, truncateFlag, forcePropertySet = false) {
			//used only when setting during initial widget rendering
			if (truncateFlag) {
				this._setTruncateState(true);
				textEditor.setTruncateOn();
				forcePropertySet && this._setProperty('truncateTitle', true); //ensure that its on on init
			} else if (truncateFlag === false) {
				this._setTruncateState(false);
				textEditor.setTruncateOff();
				this._updateModelContent && this._updateModelContent({}); //make  sure titleHtml is in synch
			}
		}

		_onSetText(textEditor) {
			if (this._content) {
				const showTitle = this._showTitle();
				const truncateTitle = this._getProperty('truncateTitle');
				if (showTitle && truncateTitle) {
					this._setTruncate(textEditor, true, true);
				}
			}
		}

		_onEditStart(textEditor) {
			//disable truncate when starting editing
			this._disableTruncateOnManualEditStart(textEditor);
		}

		_onEditUpdate(currentTitle, currentTitleHtml, newTitle, transactionid) {
			//also disable smart title when editing manually
			if( currentTitle !== newTitle) {
				this._disableSmartTitleOnManualEdit(transactionid);
			}
			//title is the same, if partial formatting done, disable smart title
			const formattedPortionKey = newTitle + '</span>';
			let currentTitleHtml_ = currentTitleHtml.replace('</i>', '');
			currentTitleHtml_ = currentTitleHtml_.replace('</u>', '');
			currentTitleHtml_ = currentTitleHtml_.replace('</b>', '');
			if( currentTitleHtml_.indexOf( formattedPortionKey ) === -1 ) {
				this._disableSmartTitleOnManualEdit(transactionid);
			}
		}

		_onEditEnd(textEditor) {
			//restore truncate when stopped editing
			this._restoreTruncateAfterManualEdit(textEditor);
		}

		_disableTruncateOnManualEditStart(textEditor) {
			if (this._content && !this._truncateTemporarilyOff) {
				const showTitle = this._showTitle();
				const truncateTitle = this._content.getPropertyValue('truncateTitle');
				if (showTitle) {
					if (truncateTitle) {
						this._setTruncate(textEditor, false);
						this._truncateTemporarilyOff = true;
					}
				}
			}
		}

		_disableSmartTitleOnManualEdit(transactionid) {
			if (this._content) {
				const titleMode = this._content.getPropertyValue('titleMode');
				if (titleMode && titleMode === 'smartTitle') {
					this._content.setPropertyValue('titleMode', 'customTitle', { undoRedoTransactionId: transactionid } );
				}
			}
		}

		_restoreTruncateAfterManualEdit(textEditor) {
			if (this._content && this._truncateTemporarilyOff) {
				const showTitle = this._showTitle();
				const truncateTitle = this._content.getPropertyValue('truncateTitle');
				if (showTitle) {
					if (truncateTitle) {
						this._setTruncate(textEditor, true);
						this._truncateTemporarilyOff = false;
					}
				}
			}
		}

		// _onModifyTextColor(textEditor, addedClass, removedClass) {
		// 	textEditor.updateCssOnParagraphs(addedClass, removedClass);
		// }

	}

	return WidgetTitleTruncate;
});
